import { Check, CircleDashed, Clock, MapPin } from "lucide-react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { amountPaid, balanceDue, fmtDate, fmtMoney, fmtTime12, fmtWeekday, itinerary, lineItems, paymentSchedule, returnDate, tripTotal } from "@/lib/trip";
import { eventType } from "@/lib/eventTypes";
import { Avatar, Card, CardTitle, Logo, StatusBadge, avatarUrl } from "@/components/common";

export function QuoteTerms() {
  const terms = [
    "Confirmation requires a deposit or the required payment and written confirmation from the supplier. Until then, no space, rate, or promotion is held or reserved on your behalf.",
    "Prices, taxes, fees, promotions and availability are estimates based on information available today and may change at any time before a reservation is confirmed.",
    "Once booked, supplier terms govern the reservation, including cancellation, change and refund policies, which may include penalties. Travel protection is strongly recommended.",
    "Travelers are responsible for valid passports, visas, and any entry, health or documentation requirements for their destination.",
  ];
  return (
    <div className="border-t border-slate-100 bg-slate-50/70 px-6 py-5 sm:px-8" data-testid="quote-terms">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Terms &amp; Conditions</p>
      <p className="mt-2 text-xs font-semibold text-navy-900">This is a quote, not a confirmed booking.</p>
      <ul className="mt-2 space-y-1.5">
        {terms.map((t, i) => (
          <li key={i} className="flex gap-2 text-[11px] leading-relaxed text-slate-500"><span className="mt-[5px] h-1 w-1 shrink-0 rounded-full bg-slate-300" />{t}</li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">Questions? Reply to your advisor anytime — we're happy to walk you through the details before anything is booked.</p>
    </div>
  );
}

export function FactList({ trip, compact }) {
  const facts = [
    ["Destination", trip.destination],
    [trip.kind === "cruise" ? "Ship" : "Resort", trip.vessel],
    ["Supplier", trip.supplier],
    ["Booking #", trip.bookingNumber || trip.quoteNumber],
    ["Departure", fmtDate(trip.departure)],
    ["Return", fmtDate(returnDate(trip))],
    ["Duration", `${trip.nights} nights`],
    [trip.kind === "cruise" ? "Stateroom" : "Room", trip.room],
  ];
  return (
    <dl className={cn("grid gap-x-6", compact ? "grid-cols-2 gap-y-4" : "gap-y-3")} data-testid="trip-facts">
      {facts.map(([k, v]) => (
        <div key={k} className={cn(compact ? "min-w-0" : "flex items-baseline justify-between gap-4 border-b border-dashed border-slate-100 pb-3 last:border-0")}>
          <dt className="text-xs text-slate-500">{k}</dt>
          <dd className={cn("text-sm font-medium text-navy-900", compact ? "mt-0.5 truncate" : "text-right", k.endsWith("#") && "font-mono text-[13px]")}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ItineraryTimeline({ trip }) {
  const days = itinerary(trip);
  if (!days.length) return (
    <Card className="p-8 text-center" data-testid="itinerary-empty">
      <p className="text-sm font-medium text-navy-900">No itinerary yet</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">The day-by-day plan hasn't been added yet. Your advisor will fill this in as your trip takes shape.</p>
    </Card>
  );
  return (
    <ol className="space-y-3" data-testid="itinerary-timeline">
      {days.map((d) => (
        <li key={d.day} className="break-inside-avoid" data-testid={`itinerary-day-${d.day}`}>
          <Card className="overflow-hidden">
            <div className="flex items-center gap-4 bg-navy-900 px-4 py-3 text-white sm:px-5">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white text-navy-900">
                <span className="text-center leading-none"><span className="block text-[9px] font-semibold uppercase tracking-widest text-navy-900/50">Day</span><span className="font-display text-base font-bold">{d.day}</span></span>
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-white">{d.title}</p>
                <p className="flex items-center gap-1 truncate text-xs text-white/70"><MapPin className="h-3 w-3 shrink-0" />{fmtWeekday(d.date)}{d.place !== d.title && ` · ${d.place}`}</p>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {!d.items.length && <li className="px-4 py-3 text-sm text-slate-400 sm:px-5">Details to come</li>}
              {d.items.map((it, j) => {
                const et = eventType(it.type);
                const Icon = et?.icon;
                return (
                  <li key={j} className="flex gap-3 px-4 py-2.5 sm:gap-4 sm:px-5" data-testid="itinerary-timeline-event">
                    <span className="w-16 shrink-0 font-mono text-xs leading-6 text-slate-400">{fmtTime12(it.time)}</span>
                    {Icon ? (
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-navy-50 text-navy-700" title={et.label} data-testid={`itinerary-event-icon-${it.type}`}><Icon className="h-3.5 w-3.5" strokeWidth={2} /></span>
                    ) : (
                      <span className="grid h-6 w-6 shrink-0 place-items-center"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" /></span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm leading-6 text-navy-900">{it.text}</span>
                      {it.conf && <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-600" data-testid="itinerary-event-conf-badge">Conf #{it.conf}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </li>
      ))}
    </ol>
  );
}

export function PrintableItinerary({ trip, settings }) {
  const days = itinerary(trip);
  return createPortal(
    <div className="print-root">
      <Card id="quote-printable" className="mx-auto max-w-4xl overflow-hidden" data-testid="client-printable-itinerary">
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 sm:px-8">
        <Logo brand="mytrip" sub="Powered by Topsail Travel" />
        <div className="text-right">
          <div className="flex items-center justify-end gap-2"><p className="font-mono text-sm text-navy-900">{trip.bookingNumber || trip.quoteNumber}</p><StatusBadge status={trip.status} /></div>
          <p className="mt-1 text-xs text-slate-500">{trip.nights} nights · {days.length} days</p>
        </div>
      </div>
      <div className="relative h-56 sm:h-72">
        <img src={trip.image || "/images/sunset.jpg"} alt={trip.destination} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/30 to-navy-950/10" />
        <div className="absolute bottom-6 left-6 right-6 text-white sm:left-8 sm:right-8">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">{trip.destination}</p>
          <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">{trip.title}</h1>
          <p className="mt-1 text-sm text-white/85">{trip.vessel} · {fmtDate(trip.departure)} – {fmtDate(returnDate(trip))}</p>
        </div>
      </div>
      {settings?.advisorName && (
        <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-4 sm:px-8">
          <Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} size="md" />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Your Travel Advisor</p>
            <p className="truncate text-sm font-semibold text-navy-900">{settings.advisorName}{settings.advisorTitle ? ` · ${settings.advisorTitle}` : ""}</p>
            <p className="truncate text-xs text-slate-500">{settings.advisorEmail}</p>
          </div>
        </div>
      )}
      <div className="p-6 sm:p-8">
        <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Day-by-Day Itinerary</h2>
        {days.length ? <ItineraryTimeline trip={trip} /> : <p className="text-sm text-slate-500">The day-by-day plan hasn't been added yet.</p>}
      </div>
    </Card>
    </div>,
    document.body
  );
}

export function InvoiceTotals({ trip, extraPaid = 0 }) {
  const total = tripTotal(trip);
  const paid = amountPaid(trip, extraPaid);
  const pct = total ? Math.round((paid / total) * 100) : 0;
  return (
    <div data-testid="invoice-totals">
      <div className="grid grid-cols-3 gap-3">
        {[["Trip total", total], ["Paid", paid], ["Balance", balanceDue(trip, extraPaid)]].map(([k, v]) => (
          <div key={k} className="min-w-0">
            <p className="text-xs text-slate-500">{k}</p>
            <p className="mt-0.5 truncate font-display text-lg font-bold text-navy-900 sm:text-xl" data-testid={`invoice-${k.split(" ")[0].toLowerCase()}`}>{fmtMoney(v, true)}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-slate-500">{trip.status === "QUOTE" ? "Quote — no payments yet" : `${pct}% paid`}</p>
    </div>
  );
}

export function LineItems({ trip }) {
  const items = lineItems(trip);
  return (
    <div data-testid="invoice-line-items">
      <ul className="divide-y divide-slate-100">
        {items.map((i, j) => (
          <li key={j} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-navy-900">{i.desc}</p>
              <p className="text-xs text-slate-500">{i.category} · {i.qty} × {fmtMoney(i.unit, true)}</p>
            </div>
            <p className="shrink-0 text-sm font-semibold text-navy-900">{fmtMoney(i.qty * i.unit, true)}</p>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex justify-between border-t border-slate-200 pt-3 text-sm font-bold text-navy-900">
        <span>Total</span><span>{fmtMoney(tripTotal(trip), true)}</span>
      </div>
    </div>
  );
}

const STATE = {
  paid: ["Paid", "bg-emerald-50 text-emerald-700", Check],
  "due-soon": ["Due soon", "bg-amber-50 text-amber-700", Clock],
  overdue: ["Overdue", "bg-red-50 text-red-700", Clock],
  scheduled: ["Scheduled", "bg-slate-100 text-slate-600", CircleDashed],
  pending: ["After acceptance", "bg-slate-100 text-slate-500", CircleDashed],
};

export function PaymentSchedule({ trip, extraPaid = 0 }) {
  return (
    <ul className="space-y-2.5" data-testid="payment-schedule">
      {paymentSchedule(trip, extraPaid).map((p) => {
        const [label, cls, Icon] = STATE[p.state];
        return (
          <li key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3.5 py-3">
            <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", cls)}><Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-navy-900">{p.label}</p>
              <p className="text-xs text-slate-500">{trip.status === "QUOTE" && p.id === "dep" && !trip.plan ? "Due on acceptance" : `Due ${fmtDate(p.due)}`}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-navy-900">{fmtMoney(p.amount, true)}</p>
              <span className={cn("mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold", cls)}>{label}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function PaymentHistory({ trip, auths = [] }) {
  const paid = paymentSchedule(trip).filter((p) => p.paid);
  const done = auths.filter((a) => a.status === "COMPLETE").slice().sort((a, b) => (b.completedAt || b.createdAt).localeCompare(a.completedAt || a.createdAt));
  if (!paid.length && !done.length) return <p className="py-4 text-sm text-slate-500">No payments recorded yet.</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="payment-history">
      {paid.map((p) => (
        <li key={p.id} className="flex items-center justify-between gap-4 py-3">
          <div>
            <p className="text-sm font-medium text-navy-900">{p.label}</p>
            <p className="text-xs text-slate-500">{fmtDate(p.paidOn)} · {p.method}</p>
          </div>
          <p className="text-sm font-semibold text-emerald-700">−{fmtMoney(p.amount, true)}</p>
        </li>
      ))}
      {done.map((a) => (
        <li key={a.id} className="flex items-center justify-between gap-4 py-3" data-testid={`payment-history-auth-${a.id}`}>
          <div>
            <p className="text-sm font-medium text-navy-900">{a.manual ? "Payment" : "Authorized payment"}</p>
            <p className="text-xs text-slate-500">{fmtDate((a.completedAt || a.createdAt).slice(0, 10))} · {a.cardLabel}</p>
          </div>
          <p className="text-sm font-semibold text-emerald-700">−{fmtMoney(a.amount, true)}</p>
        </li>
      ))}
    </ul>
  );
}

export function InvoicePanel({ trip, extra, extraPaid = 0 }) {
  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="space-y-5 lg:col-span-3">
        <Card><CardTitle>Invoice Summary</CardTitle><div className="p-5 sm:p-6"><InvoiceTotals trip={trip} extraPaid={extraPaid} /></div></Card>
        <Card><CardTitle>Charges</CardTitle><div className="px-5 pb-5 sm:px-6"><LineItems trip={trip} /></div></Card>
      </div>
      <div className="space-y-5 lg:col-span-2">
        <Card><CardTitle>Payment Schedule</CardTitle><div className="p-5 sm:p-6"><PaymentSchedule trip={trip} extraPaid={extraPaid} /></div></Card>
        {extra}
      </div>
    </div>
  );
}
